import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Copy, Check, Clock, ArrowLeft, AlertCircle } from 'lucide-react';
import axios from 'axios';

const BankTransferPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { serviceType, serviceDuration, amount, serviceTitle } = location.state || {};
  
  const [orderId, setOrderId] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [copiedField, setCopiedField] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState('');

  useEffect(() => {
    createPaymentOrder();
    fetchBankAccounts();
  }, []);

  useEffect(() => {
    if (deadline) {
      const interval = setInterval(() => {
        const now = new Date();
        const end = new Date(deadline);
        const diff = end - now;
        
        if (diff <= 0) {
          setTimeRemaining('Expired');
          clearInterval(interval);
        } else {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          setTimeRemaining(`${hours}h ${minutes}m`);
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [deadline]);

  const createPaymentOrder = async () => {
    try {
      const formData = new FormData();
      formData.append('service_type', serviceType || 'premium_listing');
      formData.append('service_duration', serviceDuration || 30);
      formData.append('amount', amount || 5000);
      formData.append('user_id', localStorage.getItem('userId') || 'temp_user_123');
      
      // Add listing_id if available (for featured ads)
      const listingId = location.state?.listingId;
      if (listingId) {
        formData.append('listing_id', listingId);
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/bank-transfer/create`,
        formData
      );

      setOrderId(response.data.order_id);
      setDeadline(response.data.payment_deadline);
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Failed to create payment order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/payments/bank-accounts`
      );
      setBankAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching bank accounts:', error);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleUploadProof = () => {
    navigate('/payment/upload-proof', {
      state: { orderId, amount, serviceType, serviceTitle, deadline }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Creating your payment order...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Bank Transfer Instructions</h1>
        </div>

        {/* Order Summary */}
        <Card className="mb-6 border-blue-200 bg-gradient-to-r from-blue-50 to-purple-50">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Order ID</p>
                <p className="text-lg font-bold text-blue-600">{orderId}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Amount to Transfer</p>
                <p className="text-2xl font-bold text-gray-900">Rs. {amount?.toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Payment Deadline</p>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-600" />
                  <p className="text-lg font-bold text-orange-600">{timeRemaining}</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {deadline && new Date(deadline).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bank Accounts */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Transfer to ANY of these accounts:</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {bankAccounts.map((account, index) => (
              <div
                key={index}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-400 transition-all"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-xl">
                      {account.bank_name.charAt(0)}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg text-gray-900">🏦 {account.bank_name}</h3>
                      <p className="text-sm text-gray-500">{account.branch}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600 mb-1">Account Name</p>
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-sm">{account.account_name}</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(account.account_name, `name-${index}`)}
                      >
                        {copiedField === `name-${index}` ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-3 rounded">
                    <p className="text-xs text-gray-600 mb-1">Account Number</p>
                    <div className="flex items-center justify-between">
                      <p className="font-bold text-blue-600">{account.account_number}</p>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(account.account_number, `acc-${index}`)}
                      >
                        {copiedField === `acc-${index}` ? (
                          <Check className="h-4 w-4 text-green-600" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Important Instructions */}
        <Alert className="mb-6 border-orange-200 bg-orange-50">
          <AlertCircle className="h-5 w-5 text-orange-600" />
          <AlertDescription>
            <p className="font-bold text-orange-900 mb-2">IMPORTANT INSTRUCTIONS:</p>
            <ul className="space-y-1 text-sm text-orange-800">
              <li>✓ Transfer EXACT amount: <strong>Rs. {amount?.toLocaleString()}</strong></li>
              <li>✓ Use Reference/Remarks: <strong>{orderId}</strong> (IMPORTANT!)</li>
              <li>✓ Keep your bank slip/receipt</li>
              <li>✓ Upload proof within {timeRemaining}</li>
              <li>✓ Verification takes 2-24 hours after upload</li>
            </ul>
          </AlertDescription>
        </Alert>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button
            onClick={handleUploadProof}
            className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-6 text-lg"
          >
            I've Made the Transfer - Upload Proof
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/')}
            className="px-8 py-6"
          >
            Cancel Order
          </Button>
        </div>

        {/* Help Section */}
        <Card className="mt-6 border-blue-200 bg-blue-50">
          <CardContent className="p-4">
            <p className="text-sm text-blue-900">
              <strong>Need help?</strong> Contact us at{' '}
              <a href="mailto:payments@zeilian.co" className="underline">payments@zeilian.co</a> or{' '}
              <a href="tel:+94112345678" className="underline">+94 11 234 5678</a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BankTransferPayment;